/**
 * Full address entry.
 *
 * Every field is labelled, every problem is `aria-describedby` the field it
 * concerns and marked `aria-invalid`, and the whole group is a `fieldset` with
 * a `legend` so a screen reader announces what these seven inputs belong to.
 * `autoComplete` tokens are the standard ones, because a password manager or a
 * browser autofill entry is exactly the assistive technology this form should
 * work with rather than against (Design Bible 9.1, 3.3.8).
 *
 * Nothing here reformats what is typed. See `lib/address.ts` for why.
 */

import type { ReactNode } from "react";
import type { AddressDraft, AddressProblems } from "../lib/address.js";

interface FieldSpec {
  readonly name: keyof AddressDraft;
  readonly label: string;
  readonly autoComplete: string;
  readonly maxLength: number;
  readonly optional?: boolean;
}

const FIELDS: readonly FieldSpec[] = [
  { name: "recipientName", label: "Full name", autoComplete: "shipping name", maxLength: 120 },
  {
    name: "organization",
    label: "Company",
    autoComplete: "shipping organization",
    maxLength: 120,
    optional: true
  },
  {
    name: "addressLine1",
    label: "Street address",
    autoComplete: "shipping address-line1",
    maxLength: 200
  },
  {
    name: "addressLine2",
    label: "Apartment, suite, or unit",
    autoComplete: "shipping address-line2",
    maxLength: 200,
    optional: true
  },
  { name: "locality", label: "City", autoComplete: "shipping address-level2", maxLength: 120 },
  { name: "region", label: "State", autoComplete: "shipping address-level1", maxLength: 2 },
  { name: "postalCode", label: "ZIP code", autoComplete: "shipping postal-code", maxLength: 10 }
];

export function AddressFields({
  draft,
  problems,
  onChange
}: {
  draft: AddressDraft;
  problems: AddressProblems;
  onChange: (next: AddressDraft) => void;
}): ReactNode {
  return (
    <fieldset className="fieldset address-fields">
      <legend className="t-label">Delivery address</legend>
      <p className="t-body-s t-secondary">
        United States delivery only. Use the address you will approve on PayPal. If the addresses
        differ, we will ask you to confirm before shipping.
      </p>
      {FIELDS.map((field) => {
        const problem = problems[field.name];
        const id = `address-${field.name}`;
        return (
          <div className={`field address-field address-field--${field.name}`} key={field.name}>
            <label className="t-label" htmlFor={id}>
              {field.label}
              {field.optional === true ? (
                <span className="t-body-s t-secondary"> (optional)</span>
              ) : null}
            </label>
            <input
              autoComplete={field.autoComplete}
              autoCapitalize={field.name === "region" ? "characters" : "words"}
              {...(problem === undefined
                ? {}
                : { "aria-describedby": `${id}-problem`, "aria-invalid": true })}
              id={id}
              inputMode={field.name === "postalCode" ? "numeric" : "text"}
              maxLength={field.maxLength}
              name={field.name}
              placeholder={field.name === "region" ? "CA" : undefined}
              required={field.optional !== true}
              onChange={(event) => {
                onChange({ ...draft, [field.name]: event.target.value });
              }}
              type="text"
              value={draft[field.name]}
            />
            {problem === undefined ? null : (
              <p className="field-problem" id={`${id}-problem`}>
                {problem}
              </p>
            )}
          </div>
        );
      })}
      <p className="t-body-s t-secondary">Country: United States.</p>
    </fieldset>
  );
}
